import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'rc-dropdown';

import { successToast, errorToast } from 'configuration/notifications';
import { transferApi } from 'helpers/api/transfer';
import { getUnixTimestamp } from 'helpers/utils';

import { Stock } from 'store/stock/actions';
import { Reservation } from 'store/transferAct/actions';
import { stockListSelector, stockSelector } from 'store/stock/selectors';
import { reserveSelector, reserveInitalSelector } from 'store/transferAct/selectors';

import { Header } from 'components/Header';
import { Icon } from 'components/Icon';
import { ButtonIcon } from 'components/Button';
import { Loader } from 'components/Loader';
import { HistoryPopup } from 'components/HistoryPopup';
import { ModalAttention } from 'components/ModalAttention';
import { InfoCardsSection } from './components/InfoCardsSection';
import { ReserveTable } from './components/ReserveTable';
import { ActionButtonsSection } from './components/ActionButtonsSection';

import './style.scss';
import { rejectReasonApi } from 'helpers/api/rejectReason';

const defaultReserve = { bonus: null, instruments: [], comment: '', time: null, client: null };

const view = 'view';
const edit = 'edit';
const create = 'create';

const ReservePage = ({ isEdit }) => {
  const { reserveId } = useParams();
  let state;
  if (reserveId) {
    state = isEdit ? edit : view;
  } else {
    state = create;
  }

  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const menuRef = useRef(null);

  const reserve = useSelector(reserveSelector);
  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const initalReserve = useSelector(reserveInitalSelector);

  const [showMenu, setShowMenu] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [pending, setPending] = useState(false);
  const [attentionModal, setAttentionModal] = useState(false);
  const [reload, setReload] = useState(0);
  const [rejectReason, setRejectReason] = useState([]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const getRejectReason = async () => {
    if (rejectReason.length === 0) {
      const { data } = await rejectReasonApi.getList();
      setRejectReason(data);
    }
  };

  useEffect(() => {
    if (location?.state?.fromClient) {
      if (location.state.selected) {
        if (reserve === null) {
          updateReserve({
            ...defaultReserve,
            client: location.state.selected,
          });
          return;
        }
        updateReserve({ client: location.state.selected });
        return;
      }
      return;
    } else if (location?.state?.fromMain) {
      updateReserve({ ...defaultReserve, instruments: location.state.instruments });
      dispatch(Stock.setStock(location.state.stock));
      return;
    }
    if (state === create) {
      const emptyReserve = { ...defaultReserve };
      dispatch(Reservation.saveReserve(emptyReserve));
      return;
    }
    dispatch(Reservation.getReservation(reserveId));
  }, [dispatch, reload]);

  useEffect(() => {
    if (stockList === null) {
      dispatch(Stock.getStockList());
      return;
    }
    if (reserve?.actID1C) {
      const findStock = stockList.find((item) => item.stockID1C === reserve.stockID);
      if (!findStock) {
        errorToast('У Вас нет прав для редактирования этого склада!');
        history.push('/');
        return;
      }

      if (stock?.stockID1C !== findStock?.stockID1C) {
        dispatch(Stock.setStock(findStock));
      }
    }
  }, [reserve, stockList]);

  const handleClickOutside = () => {
    if (historyModal) {
      setHistoryModal(null);
    }
  };

  const isSendViberMessage = useCallback(() => reserve?.isSendViberMessage && reserve?.isApproved === 0, [reserve]);

  const handleClose = (isSubmit) => {
    if (!isSubmit && JSON.stringify(initalReserve) !== JSON.stringify(reserve)) {
      setAttentionModal(true);
      return;
    }

    setAttentionModal(false);

    if (state === edit) {
      if (isSubmit) {
        setReload(reload + 1);
      }

      history.push(`/reservation/${reserveId}`);
    } else {
      history.push('/');
    }
  };

  const handleUpdateTable = (instrumentId, newData) => {
    const newTable = reserve.instruments.slice();
    const findIndex = newTable.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      newTable[findIndex] = newData;
      updateReserve({ instruments: newTable });
    }
  };

  const handleRemoveTable = (instrumentId) => {
    const findIndex = reserve.instruments.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      const newArray = reserve.instruments.slice();
      newArray.splice(findIndex, 1);

      updateReserve({ instruments: newArray });
    }
  };

  const handleSave = () => {
    if (!reserve.client) {
      return errorToast('Необходимо выбрать клиента');
    }
    if (!reserve.time || reserve.time < getUnixTimestamp()) {
      return errorToast('Необходимо выбрать время окончания резервации!');
    }
    if (reserve.instruments.length === 0) {
      return errorToast('Необходимо добавить хотя бы один инструмент!');
    }
    if (reserve.instruments.filter((inst) => inst.quantity <= 0).length > 0) {
      return errorToast('У всех инструментов должно быть количество больше нуля!');
    }
    if (pending) {
      return errorToast('Акт уже сохраняется!');
    }

    if (state === edit) {
      const additionalProps = {};

      if (reserve.isApproved !== 0 && reserve.time > getUnixTimestamp()) {
        additionalProps.isApproved = 0;
      }

      setPending(true);

      transferApi
        .updateTransferAct(reserveId, {
          ...additionalProps,
          stockID: stock.stockID1C,
          clientID: reserve.client.id,
          comment: reserve.comment,
          endDateTime: reserve.time,
          chosenItems: reserve.instruments,
          isDeferrer: reserve.isDeferrer,
        })
        .then(() => {
          history.push(`/reservation/${reserveId}`);
          successToast('Резервация успешно сохранена!');
          setPending(false);
          setReload(reload + 1);
        });
    } else if (state === create) {
      setPending(true);

      transferApi
        .createTransferAct({
          clientID: reserve.client.id,
          stockID: stock.stockID1C,
          AddrExp: '',
          type: 'reserve',
          status: 'Резерв',
          isApproved: 0,
          comment: reserve.comment,
          startDateTime: getUnixTimestamp(),
          endDateTime: reserve.time,
          chosenItems: reserve.instruments,
          isDeferrer: reserve.isDeferrer,
        })
        .then(() => {
          setPending(false);
          successToast('Резервация успешно добавлена!');
          history.push('/');
        });
    }
  };

  const handleInfo = () => {
    if (historyModal) {
      return;
    }

    transferApi.getTransferActHistory(reserveId).then(({ data } = {}) => {
      setHistoryModal(data.results);
    });
  };

  const handleAccept = async () => {
    if (pending) {
      return;
    }

    setPending(true);
    await dispatch(Reservation.updateIsApproved(reserveId, 1));

    setPending(false);
    history.push('/rent', {
      fromReserve: true,
    });
  };

  const handleDecline = async () => {
    if (pending) {
      return;
    }
    await getRejectReason();
    handleShowMenu();
  };

  const handleShowMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleMenuClick = ({ key }) => {
    return async () => {
      if (pending) {
        return;
      }
      handleShowMenu(false);
      setPending(true);
      await dispatch(Reservation.updateIsApproved(reserveId, -1));
      await rejectReasonApi.create({ rejectReasonID: key, reserveID: reserveId });
      setPending(false);
      successToast('Резервация успешно отклонена');
    };
  };

  const handleEdit = () => {
    if (reserveId) {
      history.push(`/reservation/${reserveId}/edit`);
    }
  };

  const handleSendViberMessage = () => {
    if (reserveId) {
      dispatch(Reservation.updateisSendViberMessage(reserveId));
      successToast('Сообщение успешно отправлено');
    }
  };

  const updateReserve = (updated) => {
    dispatch(Reservation.updateReserve(updated));
  };

  if (!reserve) {
    return <Loader />;
  }

  return (
    <>
      <Header
        title={
          state === create ? 'Новый резерв' : `Резерв до ${moment(reserve.endDateTime * 1000).format('HH:mm DD.MM')}`
        }
        onClose={() => handleClose(false)}
      >
        {reserve.isCreatedByClient && <Icon name="client-created-circle" tooltipText="Оформлен Клиентом" />}
        {reserve.isDeferrer && <Icon name="deferrer-reserve" tooltipText="Отложенный резерв" />}
      </Header>

      <main className="reserve-main" onClick={handleClickOutside}>
        {state !== view && <ActionButtonsSection />}

        <ReserveTable
          canChange={state !== view}
          instrumentsList={reserve.instruments}
          onUpdate={handleUpdateTable}
          onRemove={handleRemoveTable}
        />

        <InfoCardsSection reserve={reserve} stock={stock} showEmpty={state !== view} />

        <div className="buttons-block">
          {state !== view && (
            <ButtonIcon className="btn-main btn-save" onClick={handleSave} icon="save" text="Сохранить резерв" />
          )}

          {state === view && (
            <>
              <div className="btn-main" onClick={handleInfo}>
                <Icon name="view-info" />
                <HistoryPopup data={historyModal} onOutside={handleClickOutside} />
              </div>

              {reserve?.isApproved === 0 && (
                <>
                  <ButtonIcon className="btn-main" onClick={handleAccept} icon="view-accept" text="Создать аренду" />
                  <Dropdown
                    placement="bottomLeft"
                    visible={showMenu}
                    overlay={
                      <ul className="reject-reason-menu" ref={menuRef}>
                        {rejectReason.map((elem) => {
                          return (
                            <li key={elem.id} onClick={handleMenuClick({ key: elem.id })}>
                              {elem.title}
                            </li>
                          );
                        })}
                      </ul>
                    }
                  >
                    <ButtonIcon className="btn-main" onClick={handleDecline} icon="view-decline" text="Отклонить" />
                  </Dropdown>
                </>
              )}

              <ButtonIcon className="btn-main btn-save" onClick={handleEdit} icon="edit" text="Редактировать" />
              {isSendViberMessage() && (
                <ButtonIcon
                  className="btn-main btn-viber"
                  onClick={handleSendViberMessage}
                  icon="viber"
                  text="Отправить сообщение в Viber"
                />
              )}
            </>
          )}
        </div>
      </main>

      {attentionModal && <ModalAttention onClose={() => setAttentionModal(false)} onSubmit={() => handleClose(true)} />}
    </>
  );
};

ReservePage.propTypes = {
  isEdit: PropTypes.bool,
};

export { ReservePage };
